/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 25-08-2026
 *
 * De tijdbalk van één studentmodel (golf 3g): op de x-as het gekozen tijdvak met elke actie als
 * tikje, en daarboven twee lijnen — hoeveel ingrediënten er goed staan (groen, op de linker as)
 * en hoeveel er fout zijn (rood, op de tweede as). Vervangt de `modelaction`-directive uit ng1.
 * Intern child van `DlDashboardv2Component`.
 *
 * Waar ng1 op verzoek tekende (een naam-broadcast per model, met `setTimeout`-pauzes ertussen),
 * tekent dit component reactief: acties, fouten, periode en de klok komen als input binnen en
 * elke wijziging leidt tot een verse tekening. De canvasmaat komt van een `ResizeObserver` op de
 * houder — dat dekt meteen het wisselen van het raster. Tekenen gebeurt pas als die maat er is:
 * de eerste RO-melding komt ná de layout, en op een breedte van 0 valt er niets te tekenen.
 */
import {
    type AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    Input,
    type OnDestroy,
    signal,
    ViewChild
} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {DateTime} from 'luxon';
import type {Dashboardv2Actiedata, Dashboardv2Model, Dashboardv2Telling} from '../../services/dashboardv2.service';

/**
 * De periode die het paneel toont.
 */
export interface Dashboardv2Periode {
    start: DateTime;
    eind: DateTime;
}

/**
 * De zwevende melding bij de muispositie: het tijdstip waar de muis staat en de telling die op
 * dat moment gold. De vier randen zijn de kant waaraan de melding hangt; wat `null` is, wordt
 * niet gezet.
 */
interface ModelactiePopup {
    left: number | null;
    right: number | null;
    top: number | null;
    bottom: number | null;
    tijdstip: string;
    telling: Dashboardv2Telling | null;
}

/**
 * De telling zoals hij op één x-positie in de grafiek gold; gebruikt voor de melding onder de
 * muis.
 */
interface Tellingpunt {
    x: number;
    telling: Dashboardv2Telling;
}

/** De kleur van de lijn met de goede ingrediënten. */
const KLEUR_GOED = '#008000';

/** De kleur van de foutenlijn. */
const KLEUR_FOUT = '#FF0000';

/** De kleur van de x-as en van de actietikjes. */
const KLEUR_AS = '#000000';

/** De breedte die één y-as krijgt: de lijn plus de cijfers ernaast. */
const ASBREEDTE = 25;

/** De ruimte rechts van elke y-as. */
const AS_EXTRA = 10;

/** De breedte van een streepje op een y-as. */
const TICKBREEDTE = 10;

/** De hoogte van een actietikje; de x-as ligt op de helft daarvan. */
const TIKHOOGTE = 10;

/** De ruimte tussen een tikje en de nullijn van de assen. */
const EXTRARUIMTE = 5;

/** De schaal van de foutenas: tien fouten vullen hem. */
const FOUTSCHAAL = 10;

@Component({
    selector: 'dl-modelactie',
    standalone: true,
    templateUrl: './dl-modelactie.component.html',
    styleUrls: ['./dl-modelactie.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe]
})
export class DlModelactieComponent implements AfterViewInit, OnDestroy {

    /**
     * Het model waar deze grafiek bij hoort.
     */
    @Input({required: true})
    set model(waarde: Dashboardv2Model) {
        this._model.set(waarde);
    }
    private readonly _model = signal<Dashboardv2Model | null>(null);

    /**
     * De acties en tellingen van dit model.
     */
    @Input({required: true})
    set data(waarde: Dashboardv2Actiedata) {
        this._data.set(waarde);
    }
    private readonly _data = signal<Dashboardv2Actiedata>({acties: [], fouten: []});

    /**
     * Het tijdvak dat getoond wordt.
     */
    @Input({required: true})
    set periode(waarde: Dashboardv2Periode) {
        this._periode.set(waarde);
    }
    private readonly _periode = signal<Dashboardv2Periode | null>(null);

    /**
     * Het huidige moment in epoch-ms, bijgehouden door de klok van het paneel. Vanaf het laatste
     * meetpunt trekken we de twee lijnen hiernaartoe door.
     */
    @Input({required: true})
    set nu(waarde: number) {
        this._nu.set(waarde);
    }
    private readonly _nu = signal(0);

    /**
     * De houder van de canvas; die bepaalt de maat.
     */
    @ViewChild('houder', {static: true}) private houder!: ElementRef<HTMLDivElement>;

    /**
     * De canvas zelf.
     */
    @ViewChild('canvas', {static: true}) private canvas!: ElementRef<HTMLCanvasElement>;

    /**
     * De gemeten canvasmaat in pixels. Blijft op nul tot de eerste melding van de
     * `ResizeObserver`.
     */
    private readonly maat = signal({breedte: 0, hoogte: 0});

    /**
     * De waarnemer op de houder.
     */
    private waarnemer: ResizeObserver | null = null;

    /**
     * Per x-positie de telling die daar gold; gevuld tijdens het tekenen en gelezen door de
     * melding onder de muis.
     */
    private tellingpunten: Tellingpunt[] = [];

    /**
     * De ruimte links van de grafiek: twee y-assen met hun tussenruimte.
     */
    protected readonly xStart = 2 * ASBREEDTE + 2 * AS_EXTRA;

    /**
     * De zwevende melding, of `null` als de muis er niet is.
     */
    protected readonly popup = signal<ModelactiePopup | null>(null);

    /**
     * Het model waar deze grafiek bij hoort.
     */
    protected readonly modelrij = this._model.asReadonly();

    /**
     * De telling die aan het eind van het tijdvak gold, of `null` als er in dit model nog niets
     * geteld is.
     */
    protected readonly telling = computed(() => {
        const eind = this._periode()?.eind.toMillis();
        return eind === undefined ? null : (this._data().fouten.filter(fout => fout.tijd <= eind).at(-1)?.telling ?? null);
    });

    /**
     * Het begin van het tijdvak, zoals het onder de grafiek staat.
     */
    protected readonly startLabel = computed(() => this._periode()?.start.toFormat('d-M-yy HH:mm') ?? '');

    /**
     * Het einde van het tijdvak, zoals het onder de grafiek staat.
     */
    protected readonly eindLabel = computed(() => this._periode()?.eind.toFormat('d-M-yy HH:mm') ?? '');

    constructor() {
        //tekenen zodra er iets aan de gegevens, het tijdvak, de klok of de maat verandert
        effect(() => this.teken());
    }

    /**
     * De houder heeft zijn plek in de layout; vanaf nu volgen we zijn maat.
     */
    ngAfterViewInit(): void {
        this.waarnemer = new ResizeObserver(() => this.meet());
        this.waarnemer.observe(this.houder.nativeElement);
    }

    /**
     * Opruimen: de waarnemer blijft anders aan een verdwenen element hangen.
     */
    ngOnDestroy(): void {
        this.waarnemer?.disconnect();
        this.waarnemer = null;
    }

    /**
     * De muis beweegt over de grafiek: toon het tijdstip op die x-positie en de telling die daar
     * gold. Links van de assen valt er niets te melden.
     */
    protected opMuisbeweging(event: MouseEvent): void {
        const periode = this._periode();
        const {breedte, hoogte} = this.maat();
        if (!periode || !breedte) {
            return;
        }
        if (event.offsetX < this.xStart) {
            this.popup.set(null);
            return;
        }
        const start = periode.start.toMillis();
        const duur = periode.eind.toMillis() - start;
        const grafiekbreedte = breedte - this.xStart;
        this.popup.set({
            left: event.offsetX < (breedte / 2) ? event.offsetX : null,
            right: event.offsetX >= (breedte / 2) ? breedte - event.offsetX : null,
            top: event.offsetY < (hoogte / 2) ? event.offsetY + 20 : null,
            bottom: event.offsetY >= (hoogte / 2) ? hoogte - event.offsetY + 20 : null,
            tijdstip: DateTime.fromMillis(start + duur * (event.offsetX - this.xStart) / grafiekbreedte)
                .toFormat('d-M-yy HH:mm:ss'),
            telling: this.tellingpunten.filter(punt => punt.x <= event.offsetX).at(-1)?.telling ?? null
        });
    }

    /**
     * De muis is weg: melding weg.
     */
    protected opMuisWeg(): void {
        this.popup.set(null);
    }

    /**
     * Meet de houder op en zet de maat door. Elke maatwijziging is vanzelf een volledige
     * hertekening: het zetten van `canvas.width`/`height` wist de inhoud.
     */
    private meet(): void {
        const houder = this.houder.nativeElement;
        this.maat.set({breedte: houder.offsetWidth, hoogte: houder.offsetHeight});
    }

    /**
     * Tekent de hele grafiek: de x-as met de actietikjes, de twee y-assen en de twee lijnen,
     * doorgetrokken tot nu.
     */
    private teken(): void {
        const periode = this._periode();
        const {breedte, hoogte} = this.maat();
        if (!periode || !breedte || !hoogte) {
            return; //nog geen maat of geen tijdvak: er valt niets te tekenen
        }
        const canvas = this.canvas.nativeElement;
        canvas.width = breedte;
        canvas.height = hoogte;
        const renderer = canvas.getContext('2d');
        if (!renderer) {
            return;
        }

        const start = periode.start.toMillis();
        const eind = periode.eind.toMillis();
        const grafiekbreedte = breedte - this.xStart;
        const yRuimte = hoogte - TIKHOOGTE - EXTRARUIMTE; //altijd boven de tikjes
        const relatief = (tijd: number) => (tijd - start) / ((eind - start) || 1); //fix deling door nul
        this.tellingpunten = [];

        //de x-as
        renderer.lineWidth = 1;
        renderer.strokeStyle = KLEUR_AS;
        renderer.beginPath();
        renderer.moveTo(this.xStart, hoogte - TIKHOOGTE / 2 - 0.5);
        renderer.lineTo(breedte, hoogte - TIKHOOGTE / 2 - 0.5);
        renderer.stroke();

        //elke actie binnen het tijdvak een tikje
        for (const actie of this._data().acties) {
            if (actie.tijd > start && actie.tijd < eind) {
                const positie = Math.round(this.xStart + grafiekbreedte * relatief(actie.tijd));
                renderer.beginPath();
                renderer.moveTo(positie, hoogte);
                renderer.lineTo(positie, hoogte - TIKHOOGTE);
                renderer.stroke();
            }
        }

        //de schaal van de linker as komt uit de laatste normtelling: is er anders gaan geteld,
        //dan is de laatste de juiste
        const fouten = this._data().fouten;
        const normelementen = fouten.at(-1)?.telling.totaal ?? 10;
        const elementschaal = normelementen + (5 - normelementen % 5);
        this.tekenYAs(renderer, 0, elementschaal, KLEUR_GOED, yRuimte);
        this.tekenYAs(renderer, ASBREEDTE + AS_EXTRA, FOUTSCHAAL, KLEUR_FOUT, yRuimte);

        //en de twee lijnen. De goed-teller klopt niet helemaal — één ingrediënt kan meerdere
        //fouten hebben — maar zo is de aantal-lijn altijd bedoeld geweest
        const yVan = (telling: Dashboardv2Telling) => ({
            yAantal: Math.round((1 - Math.max(0, telling.aantal - telling.fout) / elementschaal) * yRuimte),
            yFout: Math.round((1 - telling.fout / FOUTSCHAAL) * yRuimte)
        });
        let vorigePunt: {x: number; yAantal: number; yFout: number} | null = null;
        let vorigeTelling: Dashboardv2Telling | null = null;
        for (const fout of fouten) {
            if (fout.tijd < start) {
                //nog vóór het tijdvak: dit is de stand waarmee de grafiek begint
                vorigeTelling = fout.telling;
                this.tellingpunten.push({x: this.xStart, telling: fout.telling});
                vorigePunt = {x: this.xStart, ...yVan(fout.telling)};
                continue;
            }
            if (fout.tijd > eind) {
                continue; //buiten beeld
            }
            const x = this.xStart + relatief(fout.tijd) * grafiekbreedte;
            if (vorigePunt && x < vorigePunt.x) {
                continue; //skip dubbele
            }
            if (!this.gelijk(vorigeTelling, fout.telling)) {
                vorigeTelling = fout.telling;
                this.tellingpunten.push({x, telling: fout.telling});
            }
            const punt = {x, ...yVan(fout.telling)};
            if (vorigePunt) {
                this.tekenStap(renderer, KLEUR_GOED, vorigePunt.x, vorigePunt.yAantal, x, punt.yAantal);
                this.tekenStap(renderer, KLEUR_FOUT, vorigePunt.x, vorigePunt.yFout, x, punt.yFout);
            }
            //anders is dit het begin
            vorigePunt = punt;
        }

        //doortrekken naar nu, maar niet verder dan het eind van het tijdvak
        if (vorigePunt) {
            const nuX = this.xStart + Math.min(1, relatief(this._nu())) * grafiekbreedte;
            if (nuX >= vorigePunt.x) {
                this.tekenStap(renderer, KLEUR_GOED, vorigePunt.x, vorigePunt.yAantal, nuX, vorigePunt.yAantal);
                this.tekenStap(renderer, KLEUR_FOUT, vorigePunt.x, vorigePunt.yFout, nuX, vorigePunt.yFout);
            }
        }
    }

    /**
     * Tekent één y-as met zijn streepjes en cijfers: hooguit vijf stappen, afgerond op een
     * vijftal.
     * @param renderer de tekenaar
     * @param x de linkerkant van de as
     * @param schaal de waarde die boven aan de as staat
     * @param kleur de kleur van de as, de streepjes en de cijfers
     * @param yRuimte de hoogte die de as mag gebruiken
     */
    private tekenYAs(renderer: CanvasRenderingContext2D, x: number, schaal: number, kleur: string, yRuimte: number): void {
        const asX = x + ASBREEDTE - (1 + TICKBREEDTE / 2);
        const tekstbreedte = asX - TICKBREEDTE / 2 - 5;
        renderer.lineWidth = 1;
        renderer.strokeStyle = kleur;
        renderer.fillStyle = kleur;
        renderer.font = '10px sans-serif';
        renderer.beginPath();
        renderer.moveTo(asX, yRuimte); //daaronder komt de tekst
        renderer.lineTo(asX, 0);
        renderer.stroke();

        let stap = Math.ceil(schaal / 5);
        if (stap % 5) {
            stap += (5 - stap % 5); //omhoog op een vijftal
        }
        let waarde = 0;
        while (true) {
            const y = ((1 - waarde / schaal) * yRuimte) || 1; //een streepje op nul valt anders weg
            renderer.beginPath();
            renderer.moveTo(asX - (TICKBREEDTE / 2), y);
            renderer.lineTo(asX + (TICKBREEDTE / 2), y);
            renderer.stroke();
            renderer.fillText(`${waarde}`, x + 3, y < 10 ? 10 : y + 2, tekstbreedte);
            if (waarde >= schaal) {
                break;
            }
            waarde = Math.min(waarde + stap, schaal);
        }
    }

    /**
     * Tekent één stap van een lijn: eerst horizontaal op de oude hoogte, dan verticaal naar de
     * nieuwe. Een lijn die boven de as uit zou komen wordt gestippeld op de bovenrand getekend.
     * @param renderer de tekenaar
     * @param kleur de kleur van de lijn
     * @param vanX de x waar de vorige meting stond
     * @param vanY de y van de vorige meting
     * @param naarX de x van deze meting
     * @param naarY de y van deze meting
     */
    private tekenStap(renderer: CanvasRenderingContext2D, kleur: string, vanX: number, vanY: number, naarX: number, naarY: number): void {
        renderer.strokeStyle = kleur;
        let y = vanY + 0.5;
        renderer.beginPath();
        if (y <= 0.5) {
            y = 0.5; //te hoog: stippellijn op de rand
            renderer.setLineDash([5, 2]);
        }
        renderer.moveTo(vanX + 0.5, y);
        renderer.lineTo(naarX + 0.5, y);
        renderer.stroke();
        renderer.setLineDash([]);
        if (naarY !== vanY) {
            renderer.beginPath();
            renderer.moveTo(naarX + 0.5, y);
            renderer.lineTo(naarX + 0.5, naarY + 0.5);
            renderer.stroke();
        }
    }

    /**
     * Vergelijkt twee tellingen op hun drie getallen.
     */
    private gelijk(links: Dashboardv2Telling | null, rechts: Dashboardv2Telling): boolean {
        return !!links && links.totaal === rechts.totaal && links.aantal === rechts.aantal && links.fout === rechts.fout;
    }
}
